import { IconBoxSeam, IconInfoCircle } from '@tabler/icons-react'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useFormatPrice } from '~/hooks'
import { Product } from '~/types'
import styles from './ProductDashboardBulkPricing.module.css'

export interface ProductDashboardBulkPricingProps {
  product: Product
}

export const ProductDashboardBulkPricing = ({
  product,
}: ProductDashboardBulkPricingProps) => {
  const { finalPrice, multiBuyTiers = [] } = product ?? {}
  const { formatPrice } = useFormatPrice()

  const tiers = [
    ...(finalPrice ? [{ minimumQuantity: 1, finalPrice }] : []),
    ...multiBuyTiers.filter((tier) => {
      return tier.finalPrice?.centAmount !== finalPrice?.centAmount
    }),
  ]

  if (tiers.length < 2) {
    return null
  }

  const gridTemplateColumnsCss = tiers.map(() => '1fr').join(' ')

  return (
    <div className={styles.root}>
      <Typography
        tag={TypographyTag.div}
        variant={TypographyVariant.BodyRegularBold}
        className={styles.heading}>
        <IconBoxSeam size={16} />
        <span>Bulk Pricing</span>
        <div className={styles.bulkPricingInfo}>
          <IconInfoCircle size={14} color='var(--color-pdp-teal)' />
          <Typography
            tag={TypographyTag.div}
            variant={TypographyVariant.BodySmall}
            className={styles.bulkTooltip}>
            Buy More, Save More! Bulk pricing applies automatically in cart.
          </Typography>
        </div>
      </Typography>
      <div
        className={styles.tiers}
        style={{ gridTemplateColumns: gridTemplateColumnsCss }}>
        {tiers.map((tier, index) => {
          const isLastTier = index === tiers.length - 1
          const nextTier = tiers[index + 1]
          const maximumQuantity = nextTier ? nextTier.minimumQuantity - 1 : null
          return (
            <Typography
              key={tier.minimumQuantity}
              tag={TypographyTag.div}
              variant={TypographyVariant.BodyRegular}
              className={styles.tier}>
              {isLastTier && (
                <Typography
                  tag={TypographyTag.div}
                  variant={TypographyVariant.BodySmallBold}
                  className={styles.bestValueBadge}>
                  Best Value
                </Typography>
              )}
              <Typography
                tag={TypographyTag.div}
                variant={TypographyVariant.BodyRegularBold}
                className={styles.tierQuantity}>
                Qty {tier.minimumQuantity}
                {isLastTier ? '+' : `-${maximumQuantity}`}
              </Typography>
              <div className={styles.tierPrice}>
                {formatPrice(tier.finalPrice)} / unit
              </div>
            </Typography>
          )
        })}
      </div>
    </div>
  )
}
